import type { CSSProperties } from "react";
import { etiquetaListaSat } from "../../shared/piezas/red.ts";

/**
 * Sistema de diseño de Argus Nexo — copia local de la paleta y helpers del
 * frontend AML (`app/frontend/src/ui/theme.ts`), más la capa de visualización
 * de datos propia de Nexo (paleta categórica de nodos del grafo). Se copia en
 * vez de importarse cruzado para que los dos frontends sigan siendo paquetes
 * independientes (cada uno se compila y despliega solo).
 */

export const C = {
  // Marca
  tiel: "#0F5257",
  tielDark: "#0B3E42",
  tielSoft: "#E5EEED",
  // Neutrales (crema/tinta, con sesgo cálido hacia la marca)
  bg: "#F7F4EC",
  rail: "#0B3E42",
  line: "#E5DFD0",
  ink: "#13343B",
  muted: "#4A6167",
  grayBg: "#EFEBE0",
  // Semáforo único (ok · alerta · crítico)
  green: "#1E7A4E",
  greenBg: "#E8F3EC",
  amber: "#9C6410",
  amberBg: "#FBF3E3",
  red: "#A93B2A",
  redBg: "#F9ECE9",
  // Informativo
  blue: "#3A648C",
  blueBg: "#E9EFF5",
  // Acento cálido (pupila del logo, indicador de sección activa)
  oro: "#E8C67A",
} as const;

/**
 * Paleta categórica CHART-GRADE para los nodos del grafo, anclada a los matices
 * de marca pero con lightness media y croma alto (la paleta UI de arriba es
 * demasiado oscura/gris para marcas de datos). Validada con el validador de
 * dataviz sobre la superficie crema #F7F4EC: banda de lightness, piso de croma
 * y separación CVD pasan; el oro exige etiquetas directas como refuerzo (el
 * grafo siempre etiqueta los nodos y lleva leyenda).
 */
export const NODO: Record<string, string> = {
  Person: "#0E9AA5",
  Organization: "#D9A93F",
  Company: "#4C7FB8",
  Vessel: "#2E9E63",
  Aircraft: "#8A64A8",
  CryptoWallet: "#4A6167",
  Unknown: "#4A6167",
};

export const SCHEMA_ES: Record<string, string> = {
  Person: "Persona",
  Organization: "Organización",
  Company: "Empresa",
  Vessel: "Buque",
  Aircraft: "Aeronave",
  CryptoWallet: "Wallet cripto",
  Unknown: "Desconocido",
};

export function nodoColor(schema: string): string {
  return NODO[schema] ?? NODO["Unknown"] ?? C.muted;
}

export function schemaEs(schema: string): string {
  return SCHEMA_ES[schema] ?? schema;
}

/** Tipo de mención del extractor documental (0094/0097), en español. */
export const MENCION_TIPO_ES: Record<string, string> = {
  person: "Persona",
  company: "Empresa",
  organization: "Organización",
  location: "Lugar",
  unknown: "Otro",
};

/** Estado probatorio fino (`claim_status`) del locator de una afirmación. */
export const CLAIM_STATUS_ES: Record<string, string> = {
  mentioned: "mención contextual",
  reported: "atribución periodística",
  alleged: "alegado en acusación",
  investigated: "bajo investigación",
  denied: "desmentido / disputado",
  unclear: "sin determinar",
};

/**
 * Etiqueta y tono de los códigos de programa/pseudo-programa. Cubre los
 * estados procesales 69-B y, desde 0075, los pseudo-programas de la familia
 * SAT Art. 69 general (`69_<CATEGORIA>`). Ambos son EJE PROPIO (situación
 * fiscal, §7.7 del diseño) — el eje red (sanciones OFAC/FinCEN) cae al
 * default neutro con su código tal cual.
 */
export function programaVista(programa: string): { etiqueta: string; tono: "rojo" | "ambar" | "verde" | "neutro" } {
  switch (programa) {
    case "69B_DEFINITIVO":
      return { etiqueta: "Facturas falsas: confirmada por el SAT", tono: "rojo" };
    case "69B_PRESUNTO":
      return { etiqueta: "Facturas falsas: presunta", tono: "ambar" };
    case "69B_SENTENCIA_FAVORABLE":
      return { etiqueta: "Facturas falsas: sentencia favorable", tono: "verde" };
    case "69B_DESVIRTUADO":
      return { etiqueta: "Facturas falsas: desvirtuado", tono: "verde" };
    // Familia Art. 69 general (flags, 0075). Tonos = severidad del catálogo
    // nexo.flag_categoria: fuerte→rojo, debil→ámbar suave (informativo).
    case "69_NO_LOCALIZADO":
      return { etiqueta: "69 No localizado", tono: "rojo" };
    case "69_CSD_SIN_EFECTOS":
      return { etiqueta: "CSD sin efectos", tono: "rojo" };
    case "69_SENTENCIA_DELITO_FISCAL":
      return { etiqueta: "69 Sentencia por delito fiscal", tono: "rojo" };
    case "69_FIRME":
      return { etiqueta: "69 Crédito firme", tono: "ambar" };
    case "69_EXIGIBLE":
      return { etiqueta: "69 Crédito exigible", tono: "ambar" };
    case "69_CANCELADO":
      return { etiqueta: "69 Crédito cancelado", tono: "ambar" };
    case "69_CONDONADO":
      return { etiqueta: "69 Crédito condonado", tono: "ambar" };
    case "69_REDUCCION_MULTAS_ART74":
      return { etiqueta: "69 Multas reducidas (74)", tono: "ambar" };
    case "69_CONDONADO_146B":
      return { etiqueta: "69 Condonado concurso (146-B)", tono: "ambar" };
    case "69_REDUCCION_RECARGOS_ART21":
      return { etiqueta: "69 Recargos reducidos (21)", tono: "ambar" };
    case "69_CONDONADO_DECRETO":
      return { etiqueta: "69 Condonado por decreto", tono: "ambar" };
    case "69_CONDONADO_07_15":
      return { etiqueta: "69 Condonado 2007-15", tono: "ambar" };
    case "69_CANCELADO_146A_07_15":
      return { etiqueta: "69 Cancelado 2007-15 (146-A)", tono: "ambar" };
    case "69_RETORNO_INVERSIONES":
      return { etiqueta: "69 Retorno de inversiones", tono: "ambar" };
    case "69_ENTES_PUBLICOS_OMISOS":
      return { etiqueta: "69 Ente público omiso", tono: "ambar" };
    case "69B_BIS_PERDIDAS_FISCALES":
      return { etiqueta: "Pérdidas fiscales transmitidas indebidamente", tono: "rojo" };
    default:
      return { etiqueta: programa, tono: "neutro" };
  }
}

/**
 * Vista de un flag fiscal ESTRUCTURADO (eje propio, §7.7): el tono sale de la
 * severidad del catálogo (no del código), la etiqueta de lista+categoría.
 * Complementa a `programaVista` (que mapea los códigos compat); cuando toda la
 * UI lea flags estructurados, `programaVista` vuelve a ser solo eje red.
 */
export function flagVista(f: { lista: string; categoria: string; severidad: string }): {
  etiqueta: string;
  tono: "rojo" | "ambar" | "verde" | "neutro";
} {
  const tono = f.severidad === "fuerte"
    ? "rojo" as const
    : f.severidad === "media"
    ? "ambar" as const
    : f.severidad === "exculpatoria"
    ? "verde" as const
    : "ambar" as const; // debil: informativo, no crítico
  return { etiqueta: etiquetaListaSat(f.lista, f.categoria), tono };
}

/**
 * Etiqueta de una lista del SAT con su categoría, en palabras del glosario: la lista 69-B es «facturas falsas» y su situación, «presunta»
 * o «confirmada por el SAT». Etapa 4, ajustes 4d: vive en app/shared/piezas/red.ts (la usa también el dibujo de la red de Argus Team).
 */
export { etiquetaListaSat };

/** Nombre de la autoridad para mostrar: FinCEN es «la Oficina del Tesoro contra el lavado» (glosario). */
export function autoridadEs(autoridad: string | null | undefined): string {
  if (!autoridad) return "";
  return autoridad.toLowerCase() === "fincen" ? "Oficina del Tesoro contra el lavado" : autoridad;
}

/** Color de anillo del eje propio en la red (flag fiscal), por severidad. */
export function flagAnillo(severidad: string | null): string | null {
  if (severidad === "fuerte") return C.red;
  if (severidad === "media" || severidad === "debil") return C.amber;
  return null;
}

/** Serif garalde para display — eco del wordmark ARGUS. Solo títulos y cifras clave. */
export const SERIF = '"Palatino Linotype", Palatino, "Book Antiqua", Georgia, "Times New Roman", serif';
export const SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export const st = {
  input: {
    boxSizing: "border-box",
    padding: "9px 12px",
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    fontSize: 14,
    background: "#fff",
    color: C.ink,
  } as CSSProperties,
  label: { fontSize: 12.5, fontWeight: 600, color: C.ink, display: "block", marginBottom: 4 } as CSSProperties,
  btn: {
    padding: "9px 14px",
    borderRadius: 10,
    border: `1px solid ${C.line}`,
    background: "#fff",
    color: C.ink,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  } as CSSProperties,
  btnPrim: {
    padding: "10px 16px",
    borderRadius: 10,
    border: "none",
    background: C.tiel,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  } as CSSProperties,
  tarjeta: {
    background: "#fff",
    border: `1px solid ${C.line}`,
    borderRadius: 14,
    padding: "14px 16px",
    marginBottom: 12,
  } as CSSProperties,
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: C.muted,
    margin: "0 0 8px",
  } as CSSProperties,
  /** Título de vista — serif de marca. */
  display: {
    fontFamily: SERIF,
    fontWeight: 500,
    letterSpacing: "0.01em",
  } as CSSProperties,
} as const;

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/**
 * Fecha ISO (AAAA-MM-DD o timestamp) → "17 jul 2026". Solo para RENDER.
 * Parseo textual deliberado — nada de `new Date(iso)` con su corrimiento UTC.
 */
export function fecha(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, anio, mesNum, dia] = m;
  if (anio === undefined || mesNum === undefined || dia === undefined) return iso;
  const mes = MESES_CORTOS[Number(mesNum) - 1];
  if (mes === undefined) return iso;
  return `${String(Number(dia))} ${mes} ${anio}`;
}

/** Entero con separador de miles es-MX (cifras del panorama). */
export function entero(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return n.toLocaleString("es-MX");
}
